import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  saveProfile,
  deleteProfile,
  setDefaultProfile,
  setActiveProfile,
} from '../../store/slices/alertConfigSlice';
import type { AlertConfigProfile } from '../../services/alertConfigService';
import {
  ThresholdConfig,
  ThresholdLevel,
  FrequencyWindow,
  FrequencyTimeUnit,
  DEFAULT_THRESHOLDS,
  DEFAULT_FREQUENCY_WINDOW,
  AlertBehaviorKey,
  BEHAVIOR_CLINICAL_MEANING,
} from './behaviorTypes';

interface Contact {
  id: string;
  name: string;
  role: string;
}

interface AlertConfigPanelProps {
  thresholds: ThresholdConfig;
  onThresholdsChange: (thresholds: ThresholdConfig) => void;
  contacts: Contact[];
  selectedContacts: string[];
  onSelectedContactsChange: (contactIds: string[]) => void;
  onClose: () => void;
}

// ─── Module-scope constants ──────────────────────────────────────────────────────

const BEHAVIOR_META: Record<AlertBehaviorKey, { icon: string; isDerived: boolean; label: string }> = {
  sitting:     { icon: '🪑', isDerived: false, label: 'Sitting' },
  standing:    { icon: '🧍', isDerived: false, label: 'Standing' },
  lying:       { icon: '😴', isDerived: false, label: 'Lying' },
  walking:     { icon: '🚶', isDerived: false, label: 'Walking' },
  scratching:  { icon: '✋', isDerived: false, label: 'Scratching' },
  spin:        { icon: '🔄', isDerived: true,  label: 'Spinning' },
  toilet:      { icon: '🚽', isDerived: true,  label: 'Toilet' },
  rest:        { icon: '💤', isDerived: true,  label: 'Resting' },
};

const DB_BEHAVIORS: AlertBehaviorKey[] = ['sitting', 'standing', 'lying', 'walking', 'scratching'];
const DERIVED_BEHAVIORS: AlertBehaviorKey[] = ['spin', 'toilet', 'rest'];
const ALL_BEHAVIORS: AlertBehaviorKey[] = [...DB_BEHAVIORS, ...DERIVED_BEHAVIORS];

const TIME_UNIT_OPTIONS: { value: FrequencyTimeUnit; label: string }[] = [
  { value: 'min', label: 'min' },
  { value: 'hr', label: 'hr' },
  { value: 'day', label: 'day' },
];

// ─── ThresholdLevelInput sub-component ───────────────────────────────────────────

interface ThresholdLevelInputProps {
  level: 'warning' | 'critical';
  thresholdLevel: ThresholdLevel | undefined;
  showFrequency: boolean;
  showDuration: boolean;
  onFrequencyChange: (value: number | undefined) => void;
  onFrequencyWindowChange: (window: FrequencyWindow | undefined) => void;
  onFrequencyActivate: (level: ThresholdLevel) => void;
  onFrequencyDeactivate: () => void;
  onDurationChange: (value: number | undefined) => void;
}

const ThresholdLevelInput: React.FC<ThresholdLevelInputProps> = ({
  level,
  thresholdLevel,
  showFrequency,
  showDuration,
  onFrequencyChange,
  onFrequencyWindowChange,
  onFrequencyActivate,
  onFrequencyDeactivate,
  onDurationChange,
}) => {
  const isWarning = level === 'warning';
  const bgColor = isWarning
    ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30'
    : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/30';
  const icon = isWarning ? '⚠️' : '🔴';
  const label = isWarning ? 'Warning' : 'Critical';

  return (
    <div className={`p-3 rounded-lg border ${bgColor}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-sm">{icon}</span>
        <span className={`text-xs font-semibold ${isWarning ? 'text-amber-700 dark:text-amber-400' : 'text-red-700 dark:text-red-400'}`}>
          {label} Threshold
        </span>
      </div>
      <div className="space-y-1.5">
        {showFrequency && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={thresholdLevel?.frequency != null}
              onChange={(e) => {
                if (e.target.checked) {
                  onFrequencyActivate({
                    frequency: thresholdLevel?.frequency || 10,
                    frequencyWindow: thresholdLevel?.frequencyWindow ?? { ...DEFAULT_FREQUENCY_WINDOW },
                  });
                } else {
                  // Atomic deactivate: set frequency to undefined in ONE call,
                  // preserving frequencyWindow so re-checking restores the saved unit.
                  onFrequencyDeactivate();
                }
              }}
              className="rounded border-gray-300 dark:border-slate-500 text-blue-500 focus:ring-blue-500"
            />
            <label className="text-sm text-gray-600 dark:text-gray-400 w-24">
              Frequency:
            </label>
            <input
              type="number"
              value={thresholdLevel?.frequency ?? ''}
              onChange={(e) => onFrequencyChange(e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={thresholdLevel?.frequency == null}
              min="1"
              placeholder="—"
              className="w-20 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">events /</span>
            <input
              type="number"
              value={thresholdLevel?.frequencyWindow?.value ?? 1}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value) : 1;
                onFrequencyWindowChange({ value: val >= 1 ? val : 1, unit: thresholdLevel?.frequencyWindow?.unit ?? 'hr' });
              }}
              disabled={thresholdLevel?.frequency == null}
              min="1"
              step="1"
              className="w-14 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <select
              value={thresholdLevel?.frequencyWindow?.unit ?? 'hr'}
              onChange={(e) => {
                const unit = e.target.value as FrequencyTimeUnit;
                onFrequencyWindowChange({ value: thresholdLevel?.frequencyWindow?.value ?? 1, unit });
              }}
              disabled={thresholdLevel?.frequency == null}
              className="px-1.5 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {TIME_UNIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
        {showDuration && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={thresholdLevel?.duration != null}
              onChange={(e) => onDurationChange(e.target.checked ? (thresholdLevel?.duration || 30) : undefined)}
              className="rounded border-gray-300 dark:border-slate-500 text-blue-500 focus:ring-blue-500"
            />
            <label className="text-sm text-gray-600 dark:text-gray-400 w-24">
              Duration:
            </label>
            <input
              type="number"
              value={thresholdLevel?.duration ?? ''}
              onChange={(e) => onDurationChange(e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={thresholdLevel?.duration == null}
              min="1"
              placeholder="—"
              className="w-20 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">minutes</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── BehaviorRow sub-component ────────────────────────────────────────────────────

interface BehaviorRowProps {
  behavior: AlertBehaviorKey;
  config: ThresholdConfig[AlertBehaviorKey];
  clinical: { frequency?: string; duration?: string };
  globalMergeGapSec: number;
  onToggle: (behavior: AlertBehaviorKey) => void;
  onUpdateWarning: (behavior: AlertBehaviorKey, level: ThresholdLevel | undefined) => void;
  onUpdateCritical: (behavior: AlertBehaviorKey, level: ThresholdLevel | undefined) => void;
  onUpdateMergeGap: (behavior: AlertBehaviorKey, value: number | undefined) => void;
}

const BehaviorRow: React.FC<BehaviorRowProps> = ({
  behavior,
  config,
  clinical,
  globalMergeGapSec,
  onToggle,
  onUpdateWarning,
  onUpdateCritical,
  onUpdateMergeGap,
}) => {
  const meta = BEHAVIOR_META[behavior];
  // Show frequency for all behaviors except 'rest'
  const showFrequency = behavior !== 'rest';

  const handleWarningFreq = (value: number | undefined) => {
    const current = config.warning ?? {};
    if (value == null && current.duration == null) {
      onUpdateWarning(behavior, undefined);
    } else {
      onUpdateWarning(behavior, { ...current, frequency: value });
    }
  };

  const handleWarningFreqWindow = (window: FrequencyWindow | undefined) => {
    const current = config.warning ?? {};
    onUpdateWarning(behavior, { ...current, frequencyWindow: window });
  };

  const handleWarningFreqActivate = (partial: ThresholdLevel) => {
    const current = config.warning ?? {};
    onUpdateWarning(behavior, { ...current, ...partial });
  };

  // Atomic deactivate: set frequency to undefined while preserving frequencyWindow
  // (and duration). If nothing else remains, drop the whole level to undefined.
  const handleWarningFreqDeactivate = () => {
    const current = config.warning ?? {};
    if (current.duration == null) {
      onUpdateWarning(behavior, undefined);
    } else {
      onUpdateWarning(behavior, { ...current, frequency: undefined });
    }
  };

  const handleWarningDur = (value: number | undefined) => {
    const current = config.warning ?? {};
    if (value == null && current.frequency == null) {
      onUpdateWarning(behavior, undefined);
    } else {
      onUpdateWarning(behavior, { ...current, duration: value });
    }
  };

  const handleCriticalFreq = (value: number | undefined) => {
    const current = config.critical ?? {};
    if (value == null && current.duration == null) {
      onUpdateCritical(behavior, undefined);
    } else {
      onUpdateCritical(behavior, { ...current, frequency: value });
    }
  };

  const handleCriticalFreqWindow = (window: FrequencyWindow | undefined) => {
    const current = config.critical ?? {};
    onUpdateCritical(behavior, { ...current, frequencyWindow: window });
  };

  const handleCriticalFreqActivate = (partial: ThresholdLevel) => {
    const current = config.critical ?? {};
    onUpdateCritical(behavior, { ...current, ...partial });
  };

  // Atomic deactivate: set frequency to undefined while preserving frequencyWindow
  // (and duration). If nothing else remains, drop the whole level to undefined.
  const handleCriticalFreqDeactivate = () => {
    const current = config.critical ?? {};
    if (current.duration == null) {
      onUpdateCritical(behavior, undefined);
    } else {
      onUpdateCritical(behavior, { ...current, frequency: undefined });
    }
  };

  const handleCriticalDur = (value: number | undefined) => {
    const current = config.critical ?? {};
    if (value == null && current.frequency == null) {
      onUpdateCritical(behavior, undefined);
    } else {
      onUpdateCritical(behavior, { ...current, duration: value });
    }
  };

  return (
    <div
      className={`p-4 rounded-lg border transition-colors ${
        config.enabled
          ? 'bg-gray-50 dark:bg-claude-darkBg border-gray-200 dark:border-claude-darkBorder'
          : 'bg-gray-100/50 dark:bg-slate-800/50 border-gray-300 dark:border-slate-700 opacity-60'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Enable toggle */}
        <button
          onClick={() => onToggle(behavior)}
          disabled={meta.isDerived}
          className={`mt-1 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            config.enabled
              ? 'bg-blue-500'
              : 'bg-gray-300 dark:bg-slate-600'
          } ${meta.isDerived ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          title={meta.isDerived ? 'Requires sequence detection (coming soon)' : undefined}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>

        {/* Icon, label, and thresholds */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">{meta.icon}</span>
            <span className="font-medium text-gray-900 dark:text-claude-darkText">
              {meta.label}
            </span>
            {meta.isDerived && (
              <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                Derived
              </span>
            )}
          </div>

          {/* Clinical meaning tooltip */}
          {(clinical.frequency || clinical.duration) && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {clinical.frequency && <span>{clinical.frequency}</span>}
              {clinical.frequency && clinical.duration && <span> · </span>}
              {clinical.duration && <span>{clinical.duration}</span>}
            </div>
          )}

          {/* Dual threshold inputs */}
          {config.enabled && (
            <div className="space-y-3 mt-3">
              <ThresholdLevelInput
                level="warning"
                thresholdLevel={config.warning}
                showFrequency={showFrequency}
                showDuration={true}
                onFrequencyChange={handleWarningFreq}
                onFrequencyWindowChange={handleWarningFreqWindow}
                onFrequencyActivate={handleWarningFreqActivate}
                onFrequencyDeactivate={handleWarningFreqDeactivate}
                onDurationChange={handleWarningDur}
              />
              <ThresholdLevelInput
                level="critical"
                thresholdLevel={config.critical}
                showFrequency={showFrequency}
                showDuration={true}
                onFrequencyChange={handleCriticalFreq}
                onFrequencyWindowChange={handleCriticalFreqWindow}
                onFrequencyActivate={handleCriticalFreqActivate}
                onFrequencyDeactivate={handleCriticalFreqDeactivate}
                onDurationChange={handleCriticalDur}
              />

              {/* Per-behavior merge gap override */}
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">🔗 Merge gap:</span>
                <input
                  type="number"
                  value={config.mergeGapSec ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value) : undefined;
                    onUpdateMergeGap(behavior, val != null && val >= 0 ? val : undefined);
                  }}
                  placeholder={String(globalMergeGapSec)}
                  min="0"
                  className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">sec</span>
                {config.mergeGapSec == null && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">uses global default ({globalMergeGapSec}s)</span>
                )}
                {config.mergeGapSec != null && (
                  <button
                    onClick={() => onUpdateMergeGap(behavior, undefined)}
                    className="text-[10px] text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Reset to default
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────────

const AlertConfigPanel: React.FC<AlertConfigPanelProps> = ({
  thresholds,
  onThresholdsChange,
  selectedContacts,
  onSelectedContactsChange,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const profiles = useSelector((state: RootState) => state.alertConfig.profiles) as AlertConfigProfile[];
  const activeProfileId = useSelector((state: RootState) => state.alertConfig.activeProfileId) as string | null;
  const isLoading = useSelector((state: RootState) => state.alertConfig.isLoading) as boolean;

  const [localThresholds, setLocalThresholds] = useState<ThresholdConfig>(thresholds);
  const [localSelectedContacts] = useState<string[]>(selectedContacts);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');

  const activeProfile = profiles.find(p => p.id === activeProfileId) ?? null;

  const handleSave = () => {
    onThresholdsChange(localThresholds);
    onSelectedContactsChange(localSelectedContacts);
    onClose();
  };

  const handleReset = () => {
    setLocalThresholds(DEFAULT_THRESHOLDS);
  };

  const handleProfileChange = (profileId: string) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      dispatch(setActiveProfile(profileId));
      setLocalThresholds(profile.thresholds);
    }
  };

  const handleSaveAsProfile = async () => {
    if (!newProfileName.trim()) return;
    await dispatch(saveProfile({
      name: newProfileName.trim(),
      thresholds: localThresholds,
      filters: activeProfile?.filters || {},
      activityDrop: localThresholds.activityDrop,
      mergeGapSec: localThresholds.mergeGapSec,
      isDefault: profiles.length === 0, // First profile is default
    }));
    setShowSaveAsDialog(false);
    setNewProfileName('');
  };

  const handleUpdateProfile = async () => {
    if (!activeProfile) return;
    await dispatch(saveProfile({
      id: activeProfile.id,
      name: activeProfile.name,
      thresholds: localThresholds,
      filters: activeProfile.filters,
      activityDrop: localThresholds.activityDrop,
      mergeGapSec: localThresholds.mergeGapSec,
      isDefault: activeProfile.isDefault,
    }));
  };

  const handleDeleteProfile = async (id: string) => {
    await dispatch(deleteProfile(id));
  };

  const handleSetDefault = async (id: string) => {
    await dispatch(setDefaultProfile(id));
  };

  const toggleBehavior = (behavior: AlertBehaviorKey) => {
    setLocalThresholds(prev => ({
      ...prev,
      [behavior]: {
        ...prev[behavior],
        enabled: !prev[behavior].enabled,
      },
    }));
  };

  const updateWarning = (behavior: AlertBehaviorKey, level: ThresholdLevel | undefined) => {
    setLocalThresholds(prev => ({
      ...prev,
      [behavior]: {
        ...prev[behavior],
        warning: level,
      },
    }));
  };

  const updateCritical = (behavior: AlertBehaviorKey, level: ThresholdLevel | undefined) => {
    setLocalThresholds(prev => ({
      ...prev,
      [behavior]: {
        ...prev[behavior],
        critical: level,
      },
    }));
  };

  const updateMergeGap = (behavior: AlertBehaviorKey, value: number | undefined) => {
    setLocalThresholds(prev => ({
      ...prev,
      [behavior]: {
        ...prev[behavior],
        mergeGapSec: value,
      },
    }));
  };

  // Contact selection is handled via localSelectedContacts and onSelectedContactsChange

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-claude-darkBorder">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-claude-darkText">
                Configure Alert Thresholds
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Set warning and critical thresholds for each behavior. Critical should be ≥ warning.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Profile Selector */}
          <div className="mt-3 flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Profile:</label>
            <select
              value={activeProfileId || ''}
              onChange={(e) => handleProfileChange(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Default (unsaved)</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.isDefault ? ' ★' : ''}
                </option>
              ))}
            </select>
            {activeProfile && (
              <div className="flex gap-1">
                <button
                  onClick={handleUpdateProfile}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                  title="Save current settings to this profile"
                >
                  💾 Save
                </button>
                <button
                  onClick={() => handleSetDefault(activeProfile.id)}
                  disabled={activeProfile.isDefault || isLoading}
                  className="px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-50"
                  title="Set as default profile"
                >
                  ⭐ Default
                </button>
                <button
                  onClick={() => handleDeleteProfile(activeProfile.id)}
                  disabled={activeProfile.isDefault || isLoading}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  title={activeProfile.isDefault ? 'Cannot delete default profile' : 'Delete this profile'}
                >
                  🗑️
                </button>
              </div>
            )}
            <button
              onClick={() => setShowSaveAsDialog(true)}
              className="px-3 py-1.5 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
              title="Save as a new profile"
            >
              ➕ Save As…
            </button>
          </div>
          {/* Save As Dialog */}
          {showSaveAsDialog && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <input
                type="text"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="Profile name…"
                className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-green-500"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsProfile(); }}
                autoFocus
              />
              <button
                onClick={handleSaveAsProfile}
                disabled={!newProfileName.trim() || isLoading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => { setShowSaveAsDialog(false); setNewProfileName(''); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Behavior alerts */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-claude-darkText mb-3">
                Behavior Alerts
              </h3>
              <div className="space-y-4">
                {ALL_BEHAVIORS.map((behavior) => (
                  <BehaviorRow
                    key={behavior}
                    behavior={behavior}
                    config={localThresholds[behavior]}
                    clinical={BEHAVIOR_CLINICAL_MEANING[behavior]}
                    globalMergeGapSec={localThresholds.mergeGapSec}
                    onToggle={toggleBehavior}
                    onUpdateWarning={updateWarning}
                    onUpdateCritical={updateCritical}
                    onUpdateMergeGap={updateMergeGap}
                  />
                ))}
              </div>
            </div>

            {/* Activity Drop */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-claude-darkText mb-3">
                Activity Alert
              </h3>
              <div className="p-4 bg-gray-50 dark:bg-claude-darkBg rounded-lg border border-gray-200 dark:border-claude-darkBorder">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📉</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-claude-darkText">
                      Activity Drop
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Alert when activity drops below this percentage from baseline
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Alert when &lt;</span>
                    <input
                      type="number"
                      value={localThresholds.activityDrop}
                      onChange={(e) => setLocalThresholds(prev => ({ ...prev, activityDrop: parseInt(e.target.value) || 0 }))}
                      min="0"
                      max="100"
                      className="w-20 px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Settings */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-claude-darkText mb-3">
                Timeline Settings
              </h3>
              <div className="p-4 bg-gray-50 dark:bg-claude-darkBg rounded-lg border border-gray-200 dark:border-claude-darkBorder">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔗</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-claude-darkText">
                      Default Merge Gap
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Global default for same-behavior merge gap. Individual behaviors can override this below.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Within</span>
                    <input
                      type="number"
                      value={localThresholds.mergeGapSec}
                      onChange={(e) => setLocalThresholds(prev => ({ ...prev, mergeGapSec: Math.max(0, parseInt(e.target.value) || 0) }))}
                      min="0"
                      max="9999"
                      className="w-20 px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkSurface dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">seconds</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  0 = no merging (same as Detail mode) · Default: 5s
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-claude-darkBorder flex justify-between gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Reset to Defaults
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-claude-darkText hover:bg-gray-100 dark:hover:bg-claude-darkSurfaceHover rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertConfigPanel;
